import { create } from 'zustand'
import { PARTNER_SHIPMENTS_ENABLED } from '../components/partner/portal/features'

const DEFAULT_PERMISSIONS = {
  SALES: { View: 'None', Create: 'None', Edit: 'None', Delete: 'None', Approve: 'None' },
  STOCK: { View: 'None', Create: 'None', Edit: 'None', Delete: 'None', Approve: 'None' },
  FINANCE: { View: 'None', Create: 'None', Edit: 'None', Delete: 'None', Approve: 'None' },
  PURCHASE: { View: 'None', Create: 'None', Edit: 'None', Delete: 'None', Approve: 'None' },
  CRM: { View: 'None', Create: 'None', Edit: 'None', Delete: 'None', Approve: 'None' },
  ADMIN: { View: 'None', Create: 'None', Edit: 'None', Delete: 'None', Approve: 'None' },
}

export const useAuthStore = create((set, get) => ({
  user: null,
  token: null,
  refreshToken: null,

  // Initialize from sessionStorage
  init: () => {
    try {
      const stored = sessionStorage.getItem('auth_state')
      if (stored) {
        const parsed = JSON.parse(stored)
        set({
          user: parsed.user,
          token: parsed.token,
          refreshToken: parsed.refreshToken,
        })
      }
    } catch (error) {
      console.error('Failed to restore auth state:', error)
    }
  },

  // Save state to sessionStorage
  _saveState: () => {
    const state = get()
    if (state.user && state.token) {
      sessionStorage.setItem('auth_state', JSON.stringify({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
      }))
    }
  },

  // Set login state
  setLogin: (user, token, refreshToken) => {
    set({ user, token, refreshToken })
    setTimeout(() => get()._saveState(), 0)
  },

  // Logout
  logout: () => {
    set({ user: null, token: null, refreshToken: null })
    sessionStorage.removeItem('auth_state')
  },

  // Update user
  setUser: (user) => {
    set({ user })
    setTimeout(() => get()._saveState(), 0)
  },

  // Update tokens
  setTokens: (token, refreshToken) => {
    set({ token, refreshToken })
    setTimeout(() => get()._saveState(), 0)
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    const state = get()
    return !!(state.user && state.token)
  },

  // Check permission: hasPermission(module, axis, minScope)
  // Supports page-level keys (SALES.ORDERS) and group-level keys (SALES → any SALES.* page)
  hasPermission: (module, axis = 'View', minScope = 'All') => {
    const state = get()
    if (!state.user || !state.user.permissions) return false

    const perms = state.user.permissions
    const moduleUpper = module.toUpperCase()
    const axisUpper = axis.toUpperCase()
    const scopeHierarchy = { NONE: 0, OWN: 1, TEAM: 2, ALL: 3 }
    const minScopeLevel = scopeHierarchy[minScope.toUpperCase()] || 0

    // Direct page key lookup (e.g. SALES.ORDERS)
    let modulePerms = perms[moduleUpper]

    // Group-level fallback: if key has no dot, check any page starting with that prefix
    if (!modulePerms && !moduleUpper.includes('.')) {
      const prefix = moduleUpper + '.'
      for (const key of Object.keys(perms)) {
        if (key.startsWith(prefix)) {
          const axScope = perms[key][axisUpper] || perms[key][axis] || 'NONE'
          if ((scopeHierarchy[axScope.toUpperCase()] || 0) >= minScopeLevel) {
            return true
          }
        }
      }
      return false
    }

    // İş ortağı panelinde rol satırı OLMAYAN anahtarlar (partner_service.effective_scope ile AYNI):
    // Sepet ← Siparişler mirası, Sohbet açık, sütun izinleri açık — satır yoksa eksen bazında düşülür.
    if (moduleUpper === 'PARTNER.SHIPMENTS' && !PARTNER_SHIPMENTS_ENABLED) return false
    const axisOf = (m) => perms[m]?.[axisUpper] || perms[m]?.[axis]
    let fallback = null
    if (moduleUpper === 'PARTNER.CART') fallback = axisOf('PARTNER.ORDERS') || 'NONE'
    else if (moduleUpper === 'PARTNER.PURCHASE_INVOICES' || moduleUpper === 'PARTNER.PURCHASE_DELIVERIES')
      fallback = axisOf('PARTNER.INVOICES') || 'NONE'
    else if (moduleUpper === 'PARTNER.CHAT') fallback = (axisUpper === 'VIEW' || axisUpper === 'CREATE') ? 'ALL' : 'NONE'
    else if (moduleUpper.startsWith('PARTNER.COL.')) fallback = axisUpper === 'VIEW' ? 'ALL' : 'NONE'
    if (fallback != null && state.user.role === 'PARTNER') {
      const sc = axisOf(moduleUpper) || fallback
      return (scopeHierarchy[String(sc).toUpperCase()] || 0) >= minScopeLevel
    }

    if (!modulePerms) return false

    const userScope = modulePerms[axisUpper] || modulePerms[axis] || 'None'
    const userScopeLevel = scopeHierarchy[userScope.toUpperCase()] || 0
    return userScopeLevel >= minScopeLevel
  },

  // Get user's accessible companies
  getAllowedCompanies: () => {
    const state = get()
    return state.user?.allowed_companies || []
  },

  // Get user's default company
  getDefaultCompany: () => {
    const state = get()
    return state.user?.company || '01'
  },

  // Get user's sales department
  getSalesDepartment: () => {
    const state = get()
    return state.user?.saldept || null
  },
}))
